import React, { useState, useEffect } from 'react';
import { useBars } from '../hooks/useBars';
import type { Bar } from '../hooks/useBars';
import { BarModal } from '../components/BarModal';
import { useAuthContext } from '../contexts/AuthContext';
import { logAction, getCurrentAdmin } from '../services/logger';
import { supabase } from '../lib/supabase';
import { Pencil, Trash2, Search, Star, Plus } from 'lucide-react';
import toast from 'react-hot-toast';

export function BarsPage() {
  const { canDelete } = useAuthContext();
  const { bars, loading, createBar, updateBar, deleteBar, refetch } = useBars();
  const [editBar, setEditBar] = useState<Bar | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [search, setSearch] = useState('');

  // Realtime for bars
  useEffect(() => {
    const channel = supabase.channel('bars-admin-rt')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bars' }, () => { refetch(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [refetch]);

  const filtered = search
    ? bars.filter(b => b.name.toLowerCase().includes(search.toLowerCase()) || b.address.toLowerCase().includes(search.toLowerCase()))
    : bars;

  const handleSave = async (id: string | null, data: Partial<Bar>) => {
    const admin = getCurrentAdmin();
    if (id) {
      // Update existing
      const bar = bars.find(b => b.id === id);
      await updateBar(id, data);
      await logAction('update_bar', 'bar', id, { name: bar?.name, address: bar?.address }, data,
        `${admin?.name} a modifié le bar ${data.name}`);
      toast.success('Bar mis à jour');
    } else {
      // Create new
      await createBar(data);
      await logAction('add_bar', 'bar', null, null, data,
        `${admin?.name} a ajouté le bar ${data.name}`);
      toast.success('Bar ajouté');
    }
  };

  const handleDeleteBar = async (bar: Bar) => {
    if (!canDelete) { toast.error('Seuls les super admins peuvent supprimer des bars'); return; }
    if (!window.confirm(`Supprimer le bar "${bar.name}" et toutes ses diffusions ?`)) return;
    const admin = getCurrentAdmin();
    await deleteBar(bar.id);
    await logAction('delete_bar', 'bar', bar.id, { name: bar.name, address: bar.address }, null,
      `${admin?.name} a supprimé le bar ${bar.name}`);
    toast.success('Bar supprimé');
    setEditBar(null);
  };

  const probBadge = (p: number | null) => {
    if (p == null) return <span style={{ color: 'var(--text-dim)', fontSize: '11px' }}>-</span>;
    const color = p >= 80 ? 'var(--green)' : p >= 60 ? 'var(--orange)' : 'var(--red)';
    return <span style={{ fontSize: '11px', fontWeight: 600, color }}>{p}%</span>;
  };

  return (
    <div>
      <div className="filters-row">
        <div className="filter-search">
          <Search size={16} />
          <input placeholder="Rechercher un bar..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <button className="btn btn-primary" onClick={() => setShowNew(true)}>
          <Plus size={16} /> Ajouter un bar
        </button>
        <span style={{ fontSize: '12px', color: 'var(--text-dim)', marginLeft: 'auto' }}>{filtered.length} bars</span>
      </div>

      <div className="card">
        {loading ? (
          <div className="skeleton-list">
            {[1,2,3,4].map(i => <div key={i} className="skeleton-row" />)}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: '44px' }}>Photo</th>
                <th>Nom</th>
                <th>Adresse</th>
                <th>Prob.</th>
                <th>Chaînes</th>
                <th>Note</th>
                <th>Statut</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(bar => (
                <tr key={bar.id}>
                  <td>
                    {bar.image_url ? (
                      <img src={bar.image_url} alt="" style={{ width: '36px', height: '36px', borderRadius: '6px', objectFit: 'cover' }} onError={e => (e.currentTarget.style.display = 'none')} />
                    ) : (
                      <div style={{ width: '36px', height: '36px', borderRadius: '6px', background: 'var(--bg-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: '14px', opacity: 0.3 }}>?</span>
                      </div>
                    )}
                  </td>
                  <td style={{ fontWeight: 600 }}>{bar.name}</td>
                  <td style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{bar.address}</td>
                  <td>{probBadge(bar.probability)}</td>
                  <td style={{ fontSize: '11px', color: 'var(--text-muted)', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {bar.tv_channels || '-'}
                  </td>
                  <td>
                    {bar.google_rating ? (
                      <span style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '3px', color: 'var(--accent)' }}>
                        <Star size={11} fill="currentColor" /> {bar.google_rating}
                      </span>
                    ) : <span style={{ color: 'var(--text-dim)', fontSize: '11px' }}>-</span>}
                  </td>
                  <td>
                    <span className={`conn-dot ${bar.is_online ? 'green' : 'red'}`} style={{ display: 'inline-block' }} />
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="btn-icon" onClick={() => setEditBar(bar)} title="Éditer"><Pencil size={15} /></button>
                    {canDelete && (
                      <button className="btn-icon danger" onClick={() => handleDeleteBar(bar)} title="Supprimer"><Trash2 size={15} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(editBar || showNew) && (
        <BarModal
          bar={editBar}
          isNew={showNew}
          onSave={handleSave}
          onDelete={canDelete ? handleDeleteBar : undefined}
          canDelete={canDelete}
          onClose={() => { setEditBar(null); setShowNew(false); }}
        />
      )}
    </div>
  );
}
